import * as React from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import DetailsBlock from '../components/tables/DetailsBlock';
import { DetailsValueBlock } from '../components/tables/DetailsValueBlock';
import { localizations } from '../localization/localization';
import DetailsContainer from '../components/tables/DetailsContainer';
import { UnitProperties, UnitType } from './UnitModels';
import { renderEmailDetails, renderPersonDetails, renderPhoneDetails, renderCompanyDetails, renderWebsiteDetails } from '../components/tables/DetailsHelpers';
import { toCommaDecimal } from '../utils/numberUtils';
import * as UnitStore from '../units/store/unitStore'
import { ApplicationState } from "../store";
import { connect } from 'react-redux';
import { FilterInfo, FilterMode } from '../common/models/CommonModels';
import moment from 'moment';
import { Theme, withStyles } from '@material-ui/core/styles';
import { bindActionCreators } from 'redux';
import { momentToString } from '../utils/dateUtilities';
import { RouteComponentProps } from 'react-router';
import { push } from 'react-router-redux';
import IconButton from '@material-ui/core/IconButton';
import MapIcon from '@material-ui/icons/Map';
import Tooltip from '@material-ui/core/Tooltip';
import forwardImg from "../images/forward.svg";
import emailImg from "../images/email.svg";
import { RouteName } from '../routeNames';
import UnitEmailDialog from './forms/UnitEmailDialog';

const styles = (theme: Theme) => ({
    leftIcon: {
        marginRight: theme.spacing(1),
    },
});

interface UnitProps {
    units: UnitStore.FreeUnitState;
    identifier: string;
    goTo: (location: string) => void
}

interface UnitViewState {
    filters: FilterInfo;
}

interface UnitMatchProps {
    identifier: string;
}

type UnitViewProps = UnitProps & RouteComponentProps<UnitMatchProps> & { unitActions: typeof UnitStore.actionCreators };

class UnitView extends React.Component<UnitViewProps, UnitViewState> {

    constructor(props: UnitViewProps) {
        super(props);

        this.state = {
            filters: {
                filterMode: FilterMode.All,
                fromCities: [],
                fromDate: moment().startOf("day").subtract(365, "days"),
                properties: [],
                reportNumberSearch: this.props.match.params.identifier,
                reporter: [],
                toCities: [],
                toDate: moment().startOf("day").add(365, "days"),
            },
        }
    }

    public componentDidMount() {
        this.props.unitActions.requestFreeUnits(this.state.filters, true);
    }

    public render() {
        const unit = this.props.units.data[0];
        const { unitActions, goTo, units } = this.props;
        const isEmailDialogOpen = units.isSendingEmail || units.isSendingEmailToReporter;

        function Buttons() {
            function goToMap() {
                unitActions.selectUnit(unit);
                goTo(RouteName.Map);
            }

            return(
            <Grid container direction="row" justify="flex-end">
                <Grid item>
                    <Tooltip title={localizations.actions.showOnMap}>
                        <IconButton style={{ height: 32, width: 32 }} onClick={() => goToMap()}><MapIcon color="primary" /></IconButton>
                    </Tooltip>
                </Grid>
                <Grid item style={{paddingLeft: 16}}>
                    <Tooltip title={localizations.actions.sendInfoByEmail}>
                        <IconButton style={{ height: 32, width: 32 }} onClick={() => unitActions.sendEmail(unit)}><img src={forwardImg} width='16' height='16' /></IconButton>
                    </Tooltip>
                </Grid>
                <Grid item style={{paddingLeft: 16}}>
                    <Tooltip title={localizations.actions.sendEmailToReporter}>
                        <IconButton style={{ height: 32, width: 32 }} onClick={() => unitActions.sendEmailToReporter(unit)}><img src={emailImg} width='16' height='16' /></IconButton>
                    </Tooltip>
                </Grid>
            </Grid>
            )
        }

        return (
            unit ?
                (<>
                    <Grid item>
                        <DetailsContainer>
                            <DetailsBlock
                                title={localizations.units.unitDetailsHeader}
                                rightItems={<Buttons />}>
                                <DetailsValueBlock title={localizations.common.reportNumber}>
                                    <Typography>{unit.reportNumber}</Typography>
                                </DetailsValueBlock>
                                <DetailsValueBlock title={localizations.units.readyForDelivery}>
                                    <Typography>{momentToString(unit.readyTime)}</Typography>
                                </DetailsValueBlock>
                                <DetailsValueBlock title={localizations.units.timeOfDelivery}>
                                    <Typography>{momentToString(unit.deliveryTime)}</Typography>
                                </DetailsValueBlock>
                                <DetailsValueBlock title={localizations.common.pointOfDeparture}>
                                    <Typography>{unit.departureCity.name}</Typography>
                                </DetailsValueBlock>
                                <DetailsValueBlock title={localizations.common.pointOfArrival}>
                                    <Typography>{unit.destinationCity.name}</Typography>
                                </DetailsValueBlock>
                                <DetailsValueBlock title={localizations.common.properties}>
                                    {renderProperties(unit.properties)}
                                </DetailsValueBlock>
                                {unit.trailerIdentifier && unit.trailerIdentifier.length > 0 && unit.trailerIdentifier !== UnitType.Unspecified ?
                                    <DetailsValueBlock title={unit.trailerType === UnitType.Trailer ? localizations.trucks.licensePlateNumber : localizations.units.containerNumber}>
                                        <Typography>{unit.trailerIdentifier}</Typography>
                                    </DetailsValueBlock>
                                    : null}
                                {unit.contractPrice ? <DetailsValueBlock title={localizations.common.contractPriceCol}>
                                    <Typography>{`${toCommaDecimal(unit.contractPrice)} €`}</Typography>
                                </DetailsValueBlock> : null}
                                {unit.details ? <DetailsValueBlock title={localizations.common.details}>
                                    <Typography>{unit.details}</Typography>
                                </DetailsValueBlock> : null}
                            </DetailsBlock>
                            {unit.company ? <DetailsBlock
                                title={localizations.units.companyDetailsHeader}
                            >
                                {renderPersonDetails(unit.coordinator, localizations.common.reporter)}
                                {renderCompanyDetails(unit.company)}
                                {unit.company.phoneContacts.map((item, index) => <React.Fragment key={index}>
                                    {renderPhoneDetails(item, index + 1)}
                                </React.Fragment>)}
                                {unit.company.emailContacts.map((item, index) => <React.Fragment key={index}>
                                    {renderEmailDetails(item, index + 1)}
                                </React.Fragment>)}
                                {renderWebsiteDetails(unit.company.website)}
                            </DetailsBlock> : null}
                        </DetailsContainer>
                    </Grid>
                    {isEmailDialogOpen && (
                        <UnitEmailDialog
                            isOpen={isEmailDialogOpen}
                            onSubmit={units.isSendingEmail ? unitActions.sendEmailSubmitted : unitActions.sendEmailToReporterSubmitted}
                            onCancel={units.isSendingEmail ? unitActions.sendEmailCancelled : unitActions.sendEmailToReporterCancelled}
                            toReporter={units.isSendingEmailToReporter}
                        />
                    )}
                </>)
                : null);
    }

}

const renderProperties = (properties: UnitProperties) => {
    return <Grid container direction="column">
        {properties.adr ? <Grid item><Typography>{localizations.trucks.properties.adr}</Typography></Grid> : null}
        {properties.thermal ? <Grid item><Typography>{localizations.trucks.properties.thermal}</Typography></Grid> : null}
        {properties.glass ? <Grid item><Typography>{localizations.trucks.properties.glass}</Typography></Grid> : null}
        {properties.tank ? <Grid item><Typography>{localizations.trucks.properties.tank}</Typography></Grid> : null}
        {properties.mega ? <Grid item><Typography>{localizations.trucks.properties.mega}</Typography></Grid> : null}
        {properties.explosive ? <Grid item><Typography>{localizations.trucks.properties.explosive}</Typography></Grid> : null}
        {properties.pieceGoods ? <Grid item><Typography>{localizations.trucks.properties.pieceGoods}</Typography></Grid> : null}
        {properties.bulkGoods ? <Grid item><Typography>{localizations.trucks.properties.bulkGoods}</Typography></Grid> : null}
    </Grid>
}

const mapStateToProps = (state: ApplicationState) => {
    const { units } = state;
    return { units };
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        unitActions: bindActionCreators(UnitStore.actionCreators, dispatch),
        goTo: (location: string) => dispatch(push(location)),
    }
}

export default withStyles(styles)(connect<any, any, any>(mapStateToProps, mapDispatchToProps)(UnitView));


// WEBPACK FOOTER //
// ./src/units/UnitView.tsx